/**
 * Timezone-aware date formatting for the whole portal.
 *
 * `useUserTimezone()` resolves the effective IANA zone once (user preference →
 * org default → browser → UTC), and `useDateFormat()` returns formatters
 * pre-bound to it. Components should call these instead of `new Date(x)
 * .toLocaleString()` so every surface renders in the viewer's chosen zone.
 *
 *   const { formatDateTime, timeZone, zoneLabel } = useDateFormat();
 *   <span>{formatDateTime(run.startedAt)}</span>
 */
import { useMemo } from 'react';
import { resolveTimeZone, safeZone, formatZoneLabel } from '@simcheck/shared-types';
import { useAuth } from '../contexts/AuthContext';
import {
  detectBrowserTimeZone,
  formatDate,
  formatDateTime,
  formatIsoDateTime,
  formatTime,
  type DateFormatOptions,
} from '../lib/utils';

/**
 * The effective IANA time zone for the current viewer, resolved from:
 * user preference → active-org default → browser → UTC. Always a valid zone
 * (guarded by `safeZone`).
 */
export function useUserTimezone(): string {
  // Org default comes from AuthContext, NOT RBACAuthContext: `RBACAuthProvider`
  // is never mounted (see the same note in `admin/edge/ModemsTab.tsx`), so
  // `useRBACAuthOptional()` always returns null and the org fallback silently
  // resolved to `undefined` on every render.
  const { userProfile, activeOrganization } = useAuth();
  const orgDefault = activeOrganization?.settings?.reports?.defaultTimezone;

  return useMemo(() => {
    const userPref = userProfile?.preferences?.timezone;
    return safeZone(
      resolveTimeZone({
        userPref,
        orgDefault,
        browserZone: detectBrowserTimeZone(),
      }),
    );
  }, [userProfile?.preferences?.timezone, orgDefault]);
}

export interface BoundDateFormatters {
  /** Resolved IANA zone these formatters render in. */
  timeZone: string;
  /** Human label for a "All times in …" annotation, e.g. `America/New_York (GMT-4)`. */
  zoneLabel: string;
  /** Absolute date+time in the resolved zone (e.g. "May 27, 2026, 2:34 PM"). */
  formatDateTime: (date: Date | string | number | null | undefined) => string;
  /** "2026-10-12 11:30" (24 h) in the resolved zone; `seconds` adds ":45". */
  formatIsoDateTime: (date: Date | string | number | null | undefined, seconds?: boolean) => string;
  /** Date only in the resolved zone. */
  formatDate: (date: Date | string | number | null | undefined) => string;
  /** Time only in the resolved zone. */
  formatTime: (date: Date | string | number | null | undefined) => string;
}

/**
 * Formatters pre-bound to the viewer's resolved zone. Stable across renders for
 * a given zone.
 */
export function useDateFormat(): BoundDateFormatters {
  const timeZone = useUserTimezone();

  return useMemo(() => {
    const opts: DateFormatOptions = { timeZone };
    return {
      timeZone,
      zoneLabel: formatZoneLabel(timeZone, new Date()),
      formatDateTime: (d) => formatDateTime(d, opts),
      formatIsoDateTime: (d, seconds) => formatIsoDateTime(d, { ...opts, seconds }),
      formatDate: (d) => formatDate(d, opts),
      formatTime: (d) => formatTime(d, opts),
    };
  }, [timeZone]);
}
